import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  TextField,
  InputAdornment,
  IconButton,
  Alert,
  Snackbar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DownloadIcon from '@mui/icons-material/Download';
import axios from 'axios';
import { API_BASE_URL } from '../../constants/constants';
import { jwtDecode } from 'jwt-decode';
import BackButton from '../../components/BackButton';
import { usePermissions } from '../../context/PermissionContext';
import { customerNoOf } from '../../components/CustomerReportShell';
import CustomerProfileDialog from '../../components/CustomerProfileDialog';

const TotalCustomersList = () => {
  const { can } = usePermissions();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [userLevel, setUserLevel] = useState(null);
  const [userId, setUserId] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('info');

  // Customer Detail View Modal State
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  // Dialog for displaying VPAs separately if needed
  const [vpaDialogOpen, setVpaDialogOpen] = useState(false);
  const [vpaDialogTitle, setVpaDialogTitle] = useState('');
  const [vpaDialogItems, setVpaDialogItems] = useState([]);

  useEffect(() => {
    const token = sessionStorage.getItem('token');
    if (token) {
      try {
        const decoded = jwtDecode(token);
        setUserLevel(decoded.level || 1);
        setUserId(decoded.userId || '');
      } catch (err) {
        console.error('Error decoding token:', err);
      }
    }
  }, []);

  useEffect(() => {
    if (userLevel !== null) {
      fetchCustomers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userLevel, userId]);

  const fetchCustomers = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await axios.get(`${API_BASE_URL}/customerProfiles`, {
        params: {
          level: userLevel,
          userId: userId,
          page: 1,
          limit: 300,
        },
      });
      const rawData = response.data.customers || response.data.data || [];
      const data = rawData.map((c, index) => {
        const nameParts = (c.customerName || '').trim().split(' ');
        const firstName = c.firstName || nameParts[0] || 'N/A';
        const lastName = c.lastName || nameParts.slice(1).join(' ') || '';
        return {
          ...c,
          id: c._id || c.customerId || index,
          firstName,
          lastName,
          // Customer ID shown = public 10-digit Customer No (never the database id)
          displayId: customerNoOf(c),
          fullCustomerId: String(c.customerId || c._id || ''),
        };
      });
      setCustomers(data);
    } catch (err) {
      console.error('Error fetching customers:', err);
      setError((err && err.response && err.response.data && err.response.data.message) ? err.response.data.message : 'Error fetching Q-Pay Customers data.');
      setCustomers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDetails = (customer) => {
    setSelectedCustomer(customer);
    setDetailModalOpen(true);
  };

  const maskVpa = (vpa) => {
    if (!vpa || typeof vpa !== 'string') return 'N/A';
    const parts = vpa.split('@');
    if (parts.length < 2) {
      if (vpa.length <= 4) return '****';
      return vpa.slice(0, 2) + '*'.repeat(Math.max(2, vpa.length - 4)) + vpa.slice(-2);
    }
    const name = parts[0];
    const domain = parts[1];
    const maskedName = name.length <= 2 ? name[0] + '*' : name.slice(0, 2) + '*'.repeat(Math.max(2, name.length - 2));
    return `${maskedName}@${domain}`;
  };

  const handleExportCSV = async () => {
    if (!can('customers.all.export')) return;

    if (!filteredCustomers || filteredCustomers.length === 0) {
      setSnackbarMessage('No data available to export');
      setSnackbarSeverity('warning');
      setSnackbarOpen(true);
      return;
    }

    const headers = ['SL', 'Customer ID', 'First Name', 'Last Name', 'Mobile', 'Email', 'State', 'District', 'Constituency', 'Current Pincode', 'Status'];
    const csvRows = [headers.join(',')];

    filteredCustomers.forEach((c, idx) => {
      const sl = idx + 1;
      const cId = c.customerNo || '';
      const fn = c.firstName || '';
      const ln = c.lastName || '';
      const mobile = c.customerMobile || '';
      const email = c.customerEmail || '';
      const state = c.customerState || c.stateCode || 'Kerala';
      const district = c.customerDistrictName || 'N/A';
      const constituency = c.customerTalukName || 'N/A';
      const pincode = c.customerPincode || 'N/A';
      const status = c.customerStatus || c.status || 'active';

      const row = [
        sl,
        `"${cId}"`,
        `"${fn}"`,
        `"${ln}"`,
        `"${mobile}"`,
        `"${email}"`,
        `"${state}"`,
        `"${district}"`,
        `"${constituency}"`,
        `"${pincode}"`,
        `"${status}"`,
      ];
      csvRows.push(row.join(','));
    });

    const blob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `qpay_customers_report_${Date.now()}.csv`);
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const handleOpenVpaDialog = (title, items) => {
    setVpaDialogTitle(title);
    const vpaStrings = Array.isArray(items) ? items.map(item => (typeof item === 'object' ? item.vpa : item)) : [];
    setVpaDialogItems(vpaStrings);
    setVpaDialogOpen(true);
  };

  const handleCloseVpaDialog = () => {
    setVpaDialogOpen(false);
    setVpaDialogTitle('');
    setVpaDialogItems([]);
  };

  const filteredCustomers = customers.filter((c) => {
    const statusVal = (c.customerStatus || c.status || 'active').toLowerCase();
    if (filterStatus && statusVal !== filterStatus) return false;
    if (!searchText) return true;
    const q = searchText.toLowerCase();
    return (
      (c.displayId && String(c.displayId).toLowerCase().includes(q)) ||
      (c.fullCustomerId && c.fullCustomerId.toLowerCase().includes(q)) ||
      (c.firstName && c.firstName.toLowerCase().includes(q)) ||
      (c.lastName && c.lastName.toLowerCase().includes(q)) ||
      (c.customerName && c.customerName.toLowerCase().includes(q)) ||
      (c.customerMobile && c.customerMobile.includes(searchText)) ||
      (c.customerEmail && c.customerEmail.toLowerCase().includes(q)) ||
      (c.customerPincode && String(c.customerPincode).includes(searchText)) ||
      (c.customerDistrictName && c.customerDistrictName.toLowerCase().includes(q)) ||
      (c.customerTalukName && c.customerTalukName.toLowerCase().includes(q))
    );
  });

  const columns = [
    {
      field: 'sl',
      headerName: 'SL',
      width: 70,
      sortable: false,
      renderCell: (params) => {
        const index = filteredCustomers.findIndex(c => (c.id || c._id) === (params.row.id || params.row._id));
        return (
          <Typography variant="body2" sx={{ fontWeight: 700, color: '#64748b' }}>
            {index >= 0 ? index + 1 : ''}
          </Typography>
        );
      },
    },
    {
      field: 'actions',
      headerName: 'Action',
      width: 140,
      sortable: false,
      renderCell: (params) => (
        <Button
          size="small"
          variant="outlined"
          color="info"
          startIcon={<VisibilityIcon />}
          onClick={() => handleOpenDetails(params.row)}
          sx={{ fontWeight: 600 }}
        >
          View Details
        </Button>
      ),
    },
    {
      field: 'displayId',
      headerName: 'Customer ID',
      width: 160,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontWeight: 700, color: 'primary.dark' }}>
          {params.value || 'N/A'}
        </Typography>
      ),
    },
    {
      field: 'firstName',
      headerName: 'First Name',
      width: 140,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontWeight: 600, color: '#0f172a' }}>
          {params.value || 'N/A'}
        </Typography>
      ),
    },
    {
      field: 'lastName',
      headerName: 'Last Name',
      width: 140,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontWeight: 600, color: '#334155' }}>
          {params.value || 'N/A'}
        </Typography>
      ),
    },
    {
      field: 'customerMobile',
      headerName: 'Mobile',
      width: 140,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {params.value || 'N/A'}
        </Typography>
      ),
    },
    {
      field: 'customerEmail',
      headerName: 'Email',
      width: 190,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ color: '#0369a1', fontWeight: 500 }}>
          {params.value || 'N/A'}
        </Typography>
      ),
    },
    {
      field: 'state',
      headerName: 'State',
      width: 120,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontWeight: 500, color: '#475569' }}>
          {params.row.customerState || params.row.stateCode || 'Kerala'}
        </Typography>
      ),
    },
    {
      field: 'customerDistrictName',
      headerName: 'District',
      width: 150,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {params.value || 'N/A'}
        </Typography>
      ),
    },
    {
      field: 'customerTalukName',
      headerName: 'Constituency',
      width: 150,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ color: '#475569' }}>
          {params.value || 'N/A'}
        </Typography>
      ),
    },
    {
      field: 'customerPincode',
      headerName: 'Current Pincodes',
      width: 140,
      renderCell: (params) => (
        <Chip label={params.value || 'N/A'} size="small" color="primary" variant="outlined" sx={{ fontWeight: 700 }} />
      ),
    },
  ];

  return (
    <Box sx={{ p: 3 }}>
      <BackButton />
      <br /><br />

      <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid #e2e8f0' }}>
        <Typography variant="h5" color="primary" sx={{ fontWeight: 800, mb: 0.5 }}>
          Q-Pay Customers
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          List of registered Q-Pay Customers and their profile details
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Search, Status Filter & Export CSV */}
        <Grid container spacing={2} alignItems="center" sx={{ mb: 3 }}>
          <Grid item xs={12} md={can('customers.all.export') ? 5 : 8}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by Customer ID, Name, Mobile, Email, Pincode..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
                endAdornment: searchText && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearchText('')}>
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={6} md={can('customers.all.export') ? 3 : 4}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                label="Status"
              >
                <MenuItem value="">All Status</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Export CSV Option (permission gated) */}
          {can('customers.all.export') && (
            <Grid item xs={12} md={4} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                color="success"
                startIcon={<DownloadIcon />}
                onClick={handleExportCSV}
                sx={{ fontWeight: 700, height: 40 }}
              >
                Export CSV
              </Button>
            </Grid>
          )}
        </Grid>

        <Box sx={{ height: 500, width: '100%' }}>
          <DataGrid
            rows={filteredCustomers}
            columns={columns}
            loading={loading}
            pageSize={10}
            rowsPerPageOptions={[10, 20, 50]}
            disableSelectionOnClick
            rowHeight={60}
            sx={{
              border: 'none',
              '& .MuiDataGrid-columnHeaders': {
                backgroundColor: '#f8fafc',
                fontWeight: 700,
              },
            }}
          />
        </Box>
      </Paper>

      {/* Shared customer profile synopsis dialog (eye buttons open the individual report pages) */}
      <CustomerProfileDialog
        open={detailModalOpen}
        onClose={() => setDetailModalOpen(false)}
        customer={selectedCustomer}
        canViewSensitive={can('customers.all.viewSensitive')}
      />

      {/* Dialog for displaying VPAs */}
      <Dialog open={vpaDialogOpen} onClose={handleCloseVpaDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: 'primary.main' }}>
          {vpaDialogTitle}
        </DialogTitle>
        <DialogContent dividers>
          {vpaDialogItems.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No VPA linked.
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, p: 1 }}>
              {vpaDialogItems.map((vpa, idx) => (
                <Chip key={idx} label={can('customers.all.viewSensitive') ? vpa : maskVpa(vpa)} color="primary" variant="outlined" sx={{ fontWeight: 600 }} />
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseVpaDialog} variant="contained" color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TotalCustomersList;
